/**
 * Local Notifications Service
 * 用於疫苗提醒排程（本地通知）
 */
import { LocalNotifications } from '@capacitor/local-notifications'
import type { LocalNotificationSchema } from '@capacitor/local-notifications'
import type { VaccinationRecord, NotificationTiming } from '../types'
import { addDays, setHours, setMinutes, isBefore } from 'date-fns'
import { Capacitor } from '@capacitor/core'
import { getDisplayPlannedDate } from '../utils/vaccineDate'

// Capacitor 權限狀態類型
export type PermissionStatus = 'granted' | 'denied' | 'prompt'

/**
 * 初始化通知渠道（僅 Android）
 */
export async function initializeNotificationChannel(): Promise<void> {
    if (Capacitor.getPlatform() !== 'android') {
        return
    }

    try {
        // 列出所有現有渠道
        const channels = await LocalNotifications.listChannels()
        const channelExists = channels.channels.some(ch => ch.id === 'vaccine-reminders-v2')

        // 如果渠道已存在，不需要重複創建
        if (channelExists) {
            return
        }

        // 創建新的通知渠道（使用 v2 ID）
        await LocalNotifications.createChannel({
            id: 'vaccine-reminders-v2',
            name: '疫苗提醒',
            description: '寶寶疫苗接種提醒通知',
            importance: 5, // 最高優先級
            visibility: 1, // 公開
            vibration: true,
            sound: 'default', // 明確指定使用預設音效
        })
    } catch (error) {
        console.error('創建通知渠道失敗:', error)
    }
}

/**
 * 請求本地通知權限
 */
export async function requestLocalNotificationPermission(): Promise<PermissionStatus> {
    try {
        const result = await LocalNotifications.requestPermissions()
        // 處理 prompt-with-rationale 狀態
        if (result.display === 'prompt-with-rationale') {
            return 'prompt'
        }
        return result.display as PermissionStatus
    } catch (error) {
        console.error('請求本地通知權限失敗:', error)
        return 'denied'
    }
}

/**
 * 檢查本地通知權限狀態
 */
export async function checkLocalNotificationPermission(): Promise<PermissionStatus> {
    try {
        const result = await LocalNotifications.checkPermissions()
        // 處理 prompt-with-rationale 狀態
        if (result.display === 'prompt-with-rationale') {
            return 'prompt'
        }
        return result.display as PermissionStatus
    } catch (error) {
        console.error('檢查本地通知權限失敗:', error)
        return 'denied'
    }
}

/**
 * 計算推播時間
 */
export function calculateNotificationTime(
    scheduledDate: string,
    timing: NotificationTiming
): Date | null {
    const scheduled = new Date(scheduledDate)

    switch (timing) {
        case '7days':
            // 7 天前，早上 9:00
            return setMinutes(setHours(addDays(scheduled, -7), 9), 0)

        case '1day':
            // 1 天前，早上 9:00
            return setMinutes(setHours(addDays(scheduled, -1), 9), 0)

        case 'morning':
            // 當天早上 8:00
            return setMinutes(setHours(scheduled, 8), 0)

        default:
            return null
    }
}

/**
 * 排程單一疫苗提醒通知
 */
export async function scheduleVaccineReminder(
    record: VaccinationRecord,
    babyName: string,
    vaccineName: string,
    doseLabel: string,
    timing: NotificationTiming
): Promise<void> {
    const notifyAt = calculateNotificationTime(getDisplayPlannedDate(record), timing)
    if (!notifyAt) {
        return
    }

    // 如果通知時間已過，不排程
    if (isBefore(notifyAt, new Date())) {
        return
    }

    // 計算延遲時間（毫秒）
    const delay = notifyAt.getTime() - Date.now()

    // 如果延遲時間太短（少於 5 分鐘），不排程
    const MIN_DELAY = 5 * 60 * 1000
    if (delay < MIN_DELAY) {
        return
    }

    // 建立唯一的通知 ID（使用 record ID 和 timing 的 hash）
    const notificationId = hashStringToNumber(`${record.id}-${timing}`)

    const notification: LocalNotificationSchema = {
        id: notificationId,
        title: `${babyName} 快需要打疫苗了`,
        body: `${vaccineName} ${doseLabel}，記得跟醫院診所預約`,
        schedule: {
            at: notifyAt,
            allowWhileIdle: true,
        },
        extra: {
            recordId: record.id,
            timing,
        },
        // Android 通知渠道和樣式設定
        channelId: 'vaccine-reminders-v2',
        // 小圖示（Android）
        smallIcon: 'ic_stat_icon',
    }

    try {
        await LocalNotifications.schedule({
            notifications: [notification],
        })
    } catch (error) {
        console.error('排程本地通知失敗:', error)
    }
}

/**
 * 取消所有已排程的本地通知
 */
export async function cancelAllLocalNotifications(): Promise<void> {
    try {
        const pending = await LocalNotifications.getPending()
        if (pending.notifications.length > 0) {
            await LocalNotifications.cancel({
                notifications: pending.notifications.map(n => ({ id: n.id })),
            })
        }
    } catch (error) {
        console.error('取消本地通知失敗:', error)
    }
}

/**
 * 重新排程所有疫苗提醒通知
 * 只為最近即將到來的一個疫苗設定提醒（從所有寶寶中找出最近的）
 */
export async function rescheduleAllVaccineReminders(
    records: VaccinationRecord[],
    getBabyName: (babyId: string) => string,
    getVaccineName: (vaccineId: string) => string,
    getDoseLabel: (doseId: string) => string,
    timings: NotificationTiming[]
): Promise<void> {
    // 先取消所有現有的排程
    await cancelAllLocalNotifications()

    // 只為計劃中的紀錄排程
    const activeRecords = records.filter(r => r.status === 'planned')

    if (activeRecords.length === 0) {
        return
    }

    // 找出最近即將到來的疫苗日期（從所有寶寶中找出未來的疫苗，按預計日期排序）
    const now = new Date()
    const futureRecords = activeRecords.filter(r => {
        const displayDate = new Date(getDisplayPlannedDate(r))
        return displayDate > now
    })

    if (futureRecords.length === 0) {
        return
    }

    const sortedRecords = [...futureRecords].sort((a, b) => {
        return new Date(getDisplayPlannedDate(a)).getTime() - new Date(getDisplayPlannedDate(b)).getTime()
    })

    // 找出最近的日期
    const firstRecord = sortedRecords[0]
    if (!firstRecord) return

    const nearestDate = getDisplayPlannedDate(firstRecord)

    // 找出所有在這個日期的疫苗（可能有多個寶寶）
    const recordsOnNearestDate = sortedRecords.filter(r => getDisplayPlannedDate(r) === nearestDate)

    // 先按時間點排程，再按寶寶排程（確保相同時間點的通知會連續發送）
    for (const timing of timings) {
        for (const record of recordsOnNearestDate) {
            const babyName = getBabyName(record.babyId)
            const vaccineName = getVaccineName(record.vaccineId)
            const doseLabel = getDoseLabel(record.doseId)

            await scheduleVaccineReminder(
                record,
                babyName,
                vaccineName,
                doseLabel,
                timing
            )
        }
    }
}

/**
 * 測試本地通知（立即顯示）
 */
export async function testLocalNotification(): Promise<void> {
    try {
        const permission = await checkLocalNotificationPermission()
        if (permission !== 'granted') {
            throw new Error('請先允許推播通知權限')
        }

        await LocalNotifications.schedule({
            notifications: [
                {
                    id: Date.now(),
                    title: '🧪 測試推播',
                    body: '如果你看到這個，表示推播功能正常！',
                    schedule: {
                        at: new Date(Date.now() + 1000), // 1 秒後
                        allowWhileIdle: true,
                    },
                },
            ],
        })
    } catch (error) {
        console.error('測試本地通知失敗:', error)
        throw error
    }
}

/**
 * 監聽本地通知事件
 */
export function addLocalNotificationListeners(
    onReceived?: (notification: LocalNotificationSchema) => void,
    onActionPerformed?: (notification: LocalNotificationSchema) => void
): void {
    LocalNotifications.addListener('localNotificationReceived', (notification) => {
        onReceived?.(notification)
    })

    LocalNotifications.addListener('localNotificationActionPerformed', (action) => {
        onActionPerformed?.(action.notification)
    })
}

/**
 * 將字串轉換為數字（用於生成通知 ID）
 */
function hashStringToNumber(str: string): number {
    let hash = 0
    for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i)
        hash = ((hash << 5) - hash) + char
        hash = hash & hash // Convert to 32bit integer
    }
    return Math.abs(hash)
}
